import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { BackendApiService } from 'src/app/core/services/backend-api';
import { OutputHistories } from 'src/app/shared/models/database-models';

const INITIAL_RFQ_OUTPUT = {
  error: '',
  isLoading: false,
  data: null,
};

interface Vendor {
  Name: string;
  VendorNumber: string;
  ID: Number;
}

export interface RfqItem {
  MyPurchaseDocumentId: number;
  MPDocumentNumber: string;
  RFQNumber: string;
  Version: number;
  VendorNumber?: string | null;
  RFQDeadline: Date;
  newRFQDeadline?: Date;
  UploadPath?: string | null;
  PE?: string | null;
  IsActive: boolean;
  RFQItems?: RFQItem[] | null;
  mpVendorDetails?: MPVendorDetails[] | null;
  outputHistories?: OutputHistorySummary[] | null;
  vendor?: Vendor | null;
}

interface RFQItem {
  RFQNumber: string;
  RFQItemNumber: number;
  DocumentVersion: number;
  PurchaseItemId: number;
  PurchaseItemNumber: number;
}

interface MPVendorDetails {
  PurchaseItemId?: string | null;
  VendorNumber: string;
  MPVendorDetailsId: number;
  OtherPersonEmail:string;
  OtherPersonName:string;
}

interface OutputHistorySummary {
  OutputHistoryId: number;
  VersionNumber?: number | null;
  CreatedOn: Date;
  TempStatus?: string | null;
  Type?: string | null;
}

export interface RfqOutputData {
  RFQ: RfqItem[];
  outputHistories: OutputHistories[];
}

export interface RfqOutput {
  error: string;
  isLoading: boolean;
  data: RfqOutputData | null;
}

export interface NewRFQDeadline {
  RFQNumber: string;
  RFQDeadline: Date;
  newRFQDeadline: Date;
}

@Injectable({ providedIn: 'root' })
export class RfqOutputService {
  private fetchedRfqOutput = new BehaviorSubject<RfqOutput>(INITIAL_RFQ_OUTPUT);
  public rfqOutput$ = this.fetchedRfqOutput.asObservable();

  constructor(private backendApi: BackendApiService) {}

  public get rfqOutputStatus() {
    // Includes isLoading, error, data
    return this.fetchedRfqOutput.value;
  }

  public get rfqOutputData() {
    // Includes only data
    return this.fetchedRfqOutput.value.data;
  }

  // Get latest data from backend
  async onFetchRfqOutput(MPDocumentNumber: string): Promise<RfqOutput> {
    this.fetchedRfqOutput.next({ error: '', isLoading: true, data: null });

    try {
      //TODO: test error response
      const RFQResponse: RfqOutputData['RFQ'] = await lastValueFrom(
        this.backendApi.getMyPurchaseDocuments(MPDocumentNumber, {
          IsActive: true,
        })
      );

      const outputHistoriesResponse: {OutputHistory: RfqOutputData['outputHistories']} =
      await lastValueFrom(this.backendApi.getOutputHistory(MPDocumentNumber));
      // TODO:
      // const outputHistoriesResponse = { OutputHistory: [] };

      const rfqOutput = {
        error: '',
        isLoading: false,
        data: {
          RFQ: RFQResponse,
          outputHistories: outputHistoriesResponse?.OutputHistory,
        },
      };

      this.fetchedRfqOutput.next(rfqOutput);

      return rfqOutput;
    } catch (err: any) {
      console.log('RfqOutputService onFetchRfqOutput failed due to error', err);
      const rfqOutput = {
        error: err?.message ?? 'Something went wrong.',
        isLoading: false,
        data: null,
      };
      this.fetchedRfqOutput.next(rfqOutput);

      return rfqOutput;
    }
  }

  // Set newRFQDeadline for a given RFQNumber
  setNewRFQDeadline(RFQNumber: string, newRFQDeadline: Date) {
    const data = this.rfqOutputData;

    if (!data || !Array.isArray(data.RFQ) || data.RFQ.length === 0) {
      return;
    }

    const updatedData = {
      ...data,
      RFQ: data.RFQ.map((rfqItem) => {
        const newRfqItem = { ...rfqItem };
        if (rfqItem.RFQNumber === RFQNumber) {
          newRfqItem.newRFQDeadline = newRFQDeadline;
        }
        return newRfqItem;
      }),
    };

    this.fetchedRfqOutput.next({
      error: '',
      isLoading: false,
      data: updatedData,
    });
  }

  public get newRFQDeadlines() {
    const rfqItems = this.rfqOutputData?.RFQ ?? [];

    const mapping: {
      [key: string]: NewRFQDeadline;
    } = {};

    for (const rfqItem of rfqItems) {
      const { RFQNumber, newRFQDeadline, RFQDeadline } = rfqItem ?? {};

      if (newRFQDeadline) {
        mapping[RFQNumber] = {
          RFQNumber,
          RFQDeadline,
          newRFQDeadline,
        };
      }
    }

    return Object.values(mapping);
  }
}
